import { useTranslation } from "react-i18next";
import { Check, ChevronLeft, ChevronRight, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { actionPillClass, slateCardClass } from "@/lib/card-styles";
import type { TooltipRendererParams } from "@/components/onboarding/TourEngine";

/**
 * Redesigned tour tooltip — replaces the react-joyride tooltip renderer.
 *
 * Visual enhancements over the previous version:
 * - Animated progress dots (clickable to jump between steps)
 * - Three-button footer: Back / Remind me later / Next-Finish
 * - Directional entrance animation (slides from spotlight side)
 * - Spotlight pulse ring (rendered by TourEngine)
 * - Backdrop blur on centered steps
 *
 * The TourEngine passes positioning, navigation, and step data via the
 * `TooltipRendererParams` shape.
 *
 * This is also where a step's copy becomes language: `page-tours.tsx` stores it
 * as plain data (`titleKey` + the English `title`), and the two are joined here
 * with `t(key, default)`. Resolving at render keeps `PAGE_TOURS` a stable
 * module-level object; a hook cannot be called from an object literal.
 */

const PROGRESS_DOT_BASE =
  "h-1.5 rounded-full transition-all duration-300 ease-out cursor-pointer";
const DOT_INACTIVE = "h-1.5 w-1.5 bg-stone-300 hover:bg-stone-400 dark:bg-stone-700 dark:hover:bg-stone-600";
const DOT_ACTIVE = "h-1.5 w-6 bg-gradient-to-r from-primary to-secondary";
const DOT_DONE = "h-1.5 w-1.5 bg-primary/60 hover:bg-primary/80";

export const TourTooltip = ({
  step,
  index,
  size,
  isLastStep,
  onNext,
  onBack,
  onClose,
  onSnooze,
  onJump,
}: TooltipRendererParams) => {
  const { t } = useTranslation();

  // A step without a key (none today, but the type allows it) falls through to
  // its literal copy, so the tooltip never renders blank.
  const title = step.titleKey && step.title ? t(step.titleKey, step.title) : step.title;
  const body =
    step.contentKey && typeof step.content === "string"
      ? t(step.contentKey, step.content)
      : step.content;

  const Icon = step.data?.icon;
  const PrimaryIcon = isLastStep ? Check : ChevronRight;
  const placement = step.placement ?? "bottom";

  // Directional entrance: slide from the side the tooltip appears on.
  const entranceAnim =
    placement === "top"
      ? "animate-in fade-in-0 slide-in-from-bottom-2 duration-300"
      : placement === "left"
        ? "animate-in fade-in-0 slide-in-from-right-2 duration-300"
        : placement === "right"
          ? "animate-in fade-in-0 slide-in-from-left-2 duration-300"
          : "animate-in fade-in-0 slide-in-from-top-2 duration-300";

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title ?? t("pages.onboarding.tooltip.step", "Tour step")}
      className={cn(
        slateCardClass,
        "relative w-[min(22rem,92vw)] space-y-4 p-5 outline-none",
        entranceAnim
      )}
    >
      {/* Header: icon + title, with room for the top-right controls */}
      <div className="flex items-start gap-3 pr-12">
        {Icon && (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary dark:bg-primary/20">
            <Icon className="h-5 w-5" />
          </span>
        )}
        {title && (
          <h2 className="pt-1 text-lg font-semibold leading-tight tracking-tight text-foreground">
            {title}
          </h2>
        )}
      </div>

      {/* Top-right: close (permanent dismiss) */}
      <div className="absolute right-4 top-4">
        <button
          type="button"
          onClick={onClose}
          aria-label={t("pages.onboarding.tooltip.closeTour", "Close tour")}
          className={cn(actionPillClass, "h-7 w-7")}
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Body */}
      {body && (
        <div className="text-sm leading-relaxed text-muted-foreground text-pretty">
          {body}
        </div>
      )}

      {/* Progress dots — clickable to jump between steps */}
      <div className="flex items-center gap-1.5">
        {Array.from({ length: size }, (_, i) => {
          const isActive = i === index;
          const isDone = i < index;
          return (
            <button
              key={i}
              type="button"
              onClick={() => onJump(i)}
              aria-label={t("pages.onboarding.tooltip.goToStep", "Go to step {{step}}", {
                step: i + 1,
              })}
              className={cn(
                PROGRESS_DOT_BASE,
                isActive ? DOT_ACTIVE : isDone ? DOT_DONE : DOT_INACTIVE,
              )}
            />
          );
        })}
      </div>

      {/* Footer: Back / Remind me later / Next-Finish */}
      <div className="flex flex-col-reverse gap-2 border-t border-stone-200/60 pt-4 dark:border-stone-700/60 sm:flex-row sm:items-center sm:justify-between">
        {/* Left side: Back */}
        <div className="flex sm:justify-start">
          {index > 0 && (
            <button
              type="button"
              onClick={onBack}
              aria-label={t("pages.onboarding.tooltip.previousStep", "Previous step")}
              className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md border border-input bg-background px-3 text-sm font-medium text-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
            >
              <ChevronLeft className="mr-1 h-4 w-4" />
              {t("common.actions.back", "Back")}
            </button>
          )}
        </div>

        {/* Right side: Remind me later + Next/Finish */}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:space-x-2">
          <button
            type="button"
            onClick={onSnooze}
            className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-md px-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground"
          >
            {t("pages.onboarding.tooltip.remindLater", "Remind me later")}
          </button>
          <button
            type="button"
            onClick={onNext}
            aria-label={
              isLastStep
                ? t("pages.onboarding.tooltip.finishTour", "Finish tour")
                : t("pages.onboarding.tooltip.nextStep", "Next step")
            }
            className="inline-flex h-10 items-center justify-center whitespace-nowrap rounded-lg bg-primary-strong px-4 text-sm font-semibold text-primary-foreground shadow-md shadow-primary/25 transition-all hover:shadow-lg hover:shadow-primary/30 hover:brightness-110 active:scale-[0.97]"
          >
            {isLastStep
              ? t("pages.onboarding.tooltip.gotIt", "Got it")
              : t("common.actions.next", "Next")}
            <PrimaryIcon className="ml-1.5 h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TourTooltip;